/**
 * Structural model: materials, steel sections, nodes, members, and the
 * capacity / wind formulas that ground the sim in real engineering.
 * See docs/ENGINEERING.md for sources (TIA-222, AISC section tables).
 *
 * Units are SI throughout: metres, kilograms, seconds, Newtons, Pascals.
 */

export interface Material {
  name: string;
  E: number; // Young's modulus (Pa)
  Fy: number; // yield strength (Pa)
  rho: number; // density (kg/m^3)
}

/**
 * Material catalog. The classic structural steels differ only in yield strength
 * (E ≈ 200 GPa for all steel; the grade sets Fy — see docs/ENGINEERING.md §3.1).
 * EHS guy strand is stranded (lower E) with a very high effective strength that
 * stands in for its rated breaking strength (§2.2).
 *
 * Beyond steel we carry a much wider, real-world palette so the player can feel
 * the material tradeoffs a bridge-builder game lives on. Every entry below cites
 * a real value (order-of-magnitude handbook / MatWeb / ASM figures) in a comment,
 * in SI units {E [Pa], Fy [Pa], rho [kg/m³]}. The solver keys off `Fy` as the
 * governing tensile/failure stress (tensionCapacity = A·Fy; compressionCapacity
 * feeds E + Fy into the AISC column curve). For BRITTLE materials (cast iron,
 * pasta, ice, glass…) there is no real yield plateau, so `Fy` is set to their
 * (low) fracture stress — which is exactly what should govern the sim: a tower
 * built of them snaps/buckles almost immediately. That's the fun.
 *
 * Categories are declared separately in MATERIAL_CATEGORY (below) so the designer
 * can group the now-long list without changing any of these keys/values.
 */
export const MATERIALS = {
  // --- structural steel — E≈200 GPa, grade sets Fy (docs/ENGINEERING.md §3.1) ---
  a36: { name: "A36", E: 200e9, Fy: 250e6, rho: 7850 },
  a572: { name: "A572 Gr50", E: 200e9, Fy: 345e6, rho: 7850 },
  a992: { name: "A992", E: 200e9, Fy: 345e6, rho: 7850 },

  // --- cables / high-strength strand (tension-only in use) ---
  guyStrand: { name: "EHS strand", E: 175e9, Fy: 1500e6, rho: 7850 },
  // Aramid (Kevlar) guy line — non-conductive (no RF detuning), light and very
  // strong: E ≈ 80 GPa, breaking ≈ 2800 MPa, ρ ≈ 1440 kg/m³. Used on broadcast
  // masts where steel guys would interfere with the antenna pattern.
  aramid: { name: "Kevlar (aramid)", E: 80e9, Fy: 2800e6, rho: 1440 },

  // --- other metals (MatWeb / ASM handbook values) ---
  // 6061-T6 aluminium: E≈68.9 GPa, yield≈240 MPa, ρ≈2700. Light but soft — great
  // strength/weight, poor stiffness (buckles at ~1/3 the load of a steel column).
  al6061: { name: "Aluminium 6061-T6", E: 69e9, Fy: 240e6, rho: 2700 },
  // 304 austenitic stainless: E≈193 GPa, yield≈215 MPa (annealed), ρ≈8000.
  // Corrosion-proof, similar stiffness to steel, modest yield.
  ss304: { name: "Stainless 304", E: 193e9, Fy: 215e6, rho: 8000 },
  // Ti-6Al-4V (Grade 5): E≈114 GPa, yield≈830 MPa, ρ≈4430. Aerospace alloy —
  // very strong for its weight, but expensive; stiffness a bit above half of steel.
  ti6al4v: { name: "Titanium Ti-6Al-4V", E: 114e9, Fy: 830e6, rho: 4430 },
  // Wrought iron: E≈190 GPa, yield≈210 MPa, ρ≈7700. Historic ductile iron —
  // the Eiffel-era metal (puddled iron). Slightly softer than mild steel.
  wroughtIron: { name: "Wrought iron", E: 190e9, Fy: 210e6, rho: 7700 },
  // Grey cast iron: E≈100 GPa, ρ≈7200. BRITTLE — no yield; tensile fracture only
  // ~150 MPa (strong in compression, weak in tension). Snaps rather than bends.
  castIron: { name: "Cast iron (grey)", E: 100e9, Fy: 150e6, rho: 7200 },

  // --- composites / engineered ---
  // CFRP (unidirectional carbon/epoxy, along-fibre): E≈150 GPa, tensile≈1500 MPa,
  // ρ≈1600. Stiff AND strong AND light — the modern high-performance choice.
  cfrp: { name: "Carbon fibre (CFRP)", E: 150e9, Fy: 1500e6, rho: 1600 },
  // GFRP (E-glass/epoxy, along-fibre): E≈40 GPa, tensile≈1000 MPa, ρ≈1900.
  // Cheaper than carbon, much less stiff — flexy but tough.
  gfrp: { name: "Fibreglass (GFRP)", E: 40e9, Fy: 1000e6, rho: 1900 },

  // --- natural materials (Wood Handbook / clear-wood values, along-grain) ---
  // Douglas-fir: E≈11 GPa, ρ≈500. Along-grain tension ~40 MPa (design/clear
  // values are far lower than lab peaks). Light, cheap, flexy — real timber tower.
  douglasFir: { name: "Timber (Douglas-fir)", E: 11e9, Fy: 40e6, rho: 500 },
  // White oak: E≈12 GPa, ρ≈750, along-grain ~50 MPa. Denser/stronger hardwood.
  oak: { name: "Oak (white)", E: 12e9, Fy: 50e6, rho: 750 },
  // Bamboo (Moso, along-grain): E≈18 GPa, tensile≈250 MPa, ρ≈700. Genuinely
  // excellent strength-to-weight — nature's rebar; a bamboo tower really works.
  bamboo: { name: "Bamboo (Moso)", E: 18e9, Fy: 250e6, rho: 700 },

  // --- NOVELTY: deliberately weak/brittle so a tower of them fails spectacularly ---
  // Dry durum spaghetti: E≈4 GPa, flexural strength ~30 MPa, VERY brittle, ρ≈1500
  // (dry pasta density). Classic bridge-builder joke material — snaps/buckles on
  // sight. (Gerbich/ASEE pasta-bridge studies report E ≈ 3–5 GPa, σf ≈ 25–40 MPa.)
  spaghetti: { name: "Dry spaghetti 🍝", E: 4e9, Fy: 30e6, rho: 1500 },
  // Green willow (fresh, wet): E≈4 GPa, ρ≈900, along-grain ~35 MPa but bendy and
  // weak — a "living" whippy stick that flexes wildly and can't hold a column.
  greenWillow: { name: "Green willow 🌿", E: 4e9, Fy: 35e6, rho: 900 },
  // Ice (freshwater, ~ -5°C): E≈9 GPa, ρ≈917. BRITTLE — tensile fracture only
  // ~1.5 MPa (compressive is higher but it still shatters). An ice tower is doomed.
  ice: { name: "Ice ❄️", E: 9e9, Fy: 1.5e6, rho: 917 },
  // Soda-lime glass: E≈70 GPa, ρ≈2500. Stiff but BRITTLE — practical tensile
  // strength only ~50 MPa (flaws govern); it shatters, never yields.
  glass: { name: "Glass (soda-lime)", E: 70e9, Fy: 50e6, rho: 2500 },
} satisfies Record<string, Material>;

export type MaterialKey = keyof typeof MATERIALS;

/** Broad category buckets for the designer's grouped material picker. */
export type MaterialCategory =
  | "Structural steel"
  | "Cable / strand"
  | "Metals"
  | "Composites"
  | "Natural"
  | "Novelty";

/**
 * Category for each material key, used only for UX grouping in the designer /
 * inspector (does not affect the sim). New keys should be added here too; a
 * fallback of "Metals" keeps things working if one is missed.
 */
export const MATERIAL_CATEGORY: Record<MaterialKey, MaterialCategory> = {
  a36: "Structural steel",
  a572: "Structural steel",
  a992: "Structural steel",
  guyStrand: "Cable / strand",
  aramid: "Cable / strand",
  al6061: "Metals",
  ss304: "Metals",
  ti6al4v: "Metals",
  wroughtIron: "Metals",
  castIron: "Metals",
  cfrp: "Composites",
  gfrp: "Composites",
  douglasFir: "Natural",
  oak: "Natural",
  bamboo: "Natural",
  spaghetti: "Novelty",
  greenWillow: "Novelty",
  ice: "Novelty",
  glass: "Novelty",
};

/** Display order for the category groups (structural first, fun last). */
export const MATERIAL_CATEGORY_ORDER: MaterialCategory[] = [
  "Structural steel",
  "Cable / strand",
  "Metals",
  "Composites",
  "Natural",
  "Novelty",
];

/** Default structural steel (A572 Gr50) and guy strand. */
export const STEEL: Material = MATERIALS.a572;
export const GUY_STRAND: Material = MATERIALS.guyStrand;

export interface Section {
  name: string;
  A: number; // cross-sectional area (m^2)
  I: number; // least moment of inertia (m^4) — governs buckling
  dragWidth: number; // width the wind "sees" (m), for member drag area
}

const IN4 = 4.162e-7; // 1 in^4 in m^4

/**
 * A small catalog of real sections in strength tiers (AISC pipe & angle
 * tables). Legs use pipe (large radius of gyration → good columns); bracing
 * uses angles. This is deliberately a handful of tiers, not the full catalog.
 */
export const SECTIONS = {
  // --- round steel pipe (Sch 40) — great columns, used for legs/monopoles ---
  legLight: { name: 'Pipe 2"', A: 6.9e-4, I: 0.666 * IN4, dragWidth: 0.06 },
  legMed: { name: 'Pipe 3"', A: 14.4e-4, I: 3.02 * IN4, dragWidth: 0.089 },
  legHeavy: { name: 'Pipe 4"', A: 20.5e-4, I: 7.23 * IN4, dragWidth: 0.114 },
  legXL: { name: 'Pipe 6"', A: 36.0e-4, I: 28.1 * IN4, dragWidth: 0.168 },
  legXXL: { name: 'Pipe 8"', A: 54.2e-4, I: 72.5 * IN4, dragWidth: 0.219 },

  // --- equal-leg angles (L-section) — classic lattice legs & bracing ---
  braceLight: { name: "L2x2x1/4", A: 6.09e-4, I: 0.346 * IN4, dragWidth: 0.051 },
  braceMed: { name: "L3x3x1/4", A: 9.29e-4, I: 1.24 * IN4, dragWidth: 0.076 },
  braceHeavy: { name: "L4x4x1/4", A: 12.5e-4, I: 3.0 * IN4, dragWidth: 0.102 },
  angleL5: { name: "L5x5x3/8", A: 22.6e-4, I: 8.74 * IN4, dragWidth: 0.127 },
  angleL6: { name: "L6x6x3/8", A: 28.1e-4, I: 15.4 * IN4, dragWidth: 0.152 },

  // --- solid round bar — common lattice bracing (I = πd^4/64) ---
  rod16: { name: "Ø16 rod", A: 2.01e-4, I: 3.22e-9, dragWidth: 0.016 },
  rod24: { name: "Ø24 rod", A: 4.52e-4, I: 1.63e-8, dragWidth: 0.024 },
  rod32: { name: "Ø32 rod", A: 8.04e-4, I: 5.15e-8, dragWidth: 0.032 },

  // --- flat bar (width × thickness) — light bracing; weak-axis I = w·t^3/12 ---
  flat50x6: { name: "Flat 50×6", A: 3.0e-4, I: 9.0e-10, dragWidth: 0.05 },
  flat75x8: { name: "Flat 75×8", A: 6.0e-4, I: 3.2e-9, dragWidth: 0.075 },
  flat100x10: { name: "Flat 100×10", A: 1.0e-3, I: 8.33e-9, dragWidth: 0.1 },

  // --- EHS guy strand — metallic area A = breaking strength / 1500 MPa, so
  //     tension capacity ≈ the real rated breaking load (see ENGINEERING.md §2.2)
  guy316: { name: '3/16" EHS', A: 1.19e-5, I: 1.1e-11, dragWidth: 0.0048 },
  guy14: { name: '1/4" EHS', A: 2.05e-5, I: 3.3e-11, dragWidth: 0.0064 },
  guy516: { name: '5/16" EHS', A: 3.27e-5, I: 8.5e-11, dragWidth: 0.0079 },
  guy38: { name: '3/8" EHS', A: 4.53e-5, I: 1.6e-10, dragWidth: 0.0095 },
  guy716: { name: '7/16" EHS', A: 6.17e-5, I: 3.0e-10, dragWidth: 0.0111 },
  guy12: { name: '1/2" EHS', A: 7.73e-5, I: 4.8e-10, dragWidth: 0.0127 },
} satisfies Record<string, Section>;

export type SectionKey = keyof typeof SECTIONS;

/**
 * Per-node foundation capacity data attached during compileTower for fixed base
 * rows and guy anchors. Optional so nodes without a foundation (interior nodes,
 * or towers compiled before this was wired) behave exactly as before. Kept as
 * loose types (imported lazily by the solver) so model.ts stays dependency-free.
 */
export interface NodeFoundation {
  /** Resolved geotechnical capacity bundle (bearing/uplift/sliding/overturning). */
  capacity: import("@/physics/foundation").FoundationCapacity;
  /** Series steel↔concrete connection capacity (RIGID_CONNECTION if none). */
  connection: import("@/physics/baseConnection").ConnectionCapacity;
  /** true for a guy-anchor node → releasing it sets partsLost ("Get Sued"). */
  isAnchor: boolean;
}

/** A mounted appurtenance load attached to a structure node (wind applied each step). */
export interface NodeMountedLoad {
  load: import("@/game/appurtenances").MountedLoad;
}

export interface Node {
  x: number;
  y: number;
  z: number;
  px: number; // previous position (velocity is derived from position delta)
  py: number;
  pz: number;
  vx: number;
  vy: number;
  vz: number;
  mass: number;
  invMass: number; // 0 == pinned/fixed
  fixed: boolean;
  /** Optional foundation capacity (set on fixed base/anchor nodes by compileTower). */
  foundationCap?: NodeFoundation;
  /** Optional mounted appurtenance load(s) whose wind force the solver applies. */
  mountedLoads?: NodeMountedLoad[];
}

export interface Member {
  i: number; // node index
  j: number; // node index
  section: Section;
  material: Material;
  L0: number; // rest (unstressed) length (m)
  k: number; // axial stiffness EA/L0 (N/m)
  Kfactor: number; // effective-length factor for buckling (1.0 pinned-pinned)
  tensionOnly: boolean; // cables/guys: carry tension only, go slack in compression
  pretension: number; // installed pretension strain (guys) — ramped in during warm-up
  // live state, updated each solve:
  force: number; // axial force N (tension +, compression -)
  util: number; // |force| / capacity ; >= 1 means failed
  slack: boolean; // tension-only member currently carrying no load
  failed: boolean;
  // how it broke: tension snap / compression buckle / bolted-connection shear-out
  // (the end connection let go before the member yielded — doc 17).
  failMode: "snap" | "buckle" | "shear" | "fatigue" | null;
  overStress: number; // seconds spent continuously over capacity (failure debounce)
  // fatigue (Miner cyclic-damage accumulation — doc 19):
  fatigue: number; // accumulated damage 0..1; ≥1 = fatigue crack-through
  fMean: number; // running mean axial force (N) — the fluctuation is measured about this
  fAmp: number; // tracked cyclic stress amplitude (N), slowly decaying
}

export interface Structure {
  nodes: Node[];
  members: Member[];
}

export function makeNode(x: number, y: number, z: number, fixed = false): Node {
  return {
    x, y, z,
    px: x, py: y, pz: z,
    vx: 0, vy: 0, vz: 0,
    mass: 0, invMass: 0, fixed,
  };
}

export interface MemberOptions {
  material?: Material;
  Kfactor?: number;
  tensionOnly?: boolean;
  /**
   * Pretension strain for cables: the rest length is shortened by this fraction
   * so the guy is installed taut (e.g. 0.001 → carries EA·0.001 of tension at
   * its installed geometry). See docs/ENGINEERING.md §2.3.
   */
  pretensionStrain?: number;
}

export function addMember(
  s: Structure,
  i: number,
  j: number,
  section: Section,
  opts: MemberOptions = {},
): Member {
  const { material = STEEL, Kfactor = 1.0, tensionOnly = false, pretensionStrain = 0 } = opts;
  const a = s.nodes[i]!;
  const b = s.nodes[j]!;
  const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  const L0 = len * (1 - pretensionStrain);
  const m: Member = {
    i, j, section, material, L0,
    k: (material.E * section.A) / L0,
    Kfactor, tensionOnly,
    pretension: pretensionStrain,
    force: 0, util: 0, slack: false, failed: false, failMode: null, overStress: 0,
    fatigue: 0, fMean: 0, fAmp: 0,
  };
  s.members.push(m);
  return m;
}

/**
 * Distribute each member's steel mass to its two end nodes (tributary mass),
 * then finalise invMass. Fixed nodes get invMass 0. Call after all members
 * are added and before simulating.
 */
export function computeMasses(s: Structure, minNodeMass = 2): void {
  for (const n of s.nodes) n.mass = 0;
  for (const m of s.members) {
    const mass = m.section.A * m.L0 * m.material.rho;
    s.nodes[m.i]!.mass += mass * 0.5;
    s.nodes[m.j]!.mass += mass * 0.5;
  }
  for (const n of s.nodes) {
    n.mass = Math.max(n.mass, minNodeMass);
    n.invMass = n.fixed ? 0 : 1 / n.mass;
  }
}

// --- member capacities (see docs/ENGINEERING.md §4) ---

/** Tension yield capacity Pt = A·Fy (N). */
export function tensionCapacity(m: Member): number {
  return m.section.A * m.material.Fy;
}

/**
 * Compression capacity via the AISC 360 / TIA-222 inelastic COLUMN CURVE
 * (docs/engineering 04 & 07). Instead of a crude min(Euler, yield), the strength
 * transitions smoothly with slenderness λ = KL/r:
 *   - stocky (λ ≤ 4.71·√(E/Fy)) → inelastic buckling `Fcr = 0.658^(Fy/Fe)·Fy`,
 *     which sits BELOW yield for real intermediate columns
 *   - slender → elastic buckling `Fcr = 0.877·Fe` (0.877 knocks Euler down for
 *     out-of-straightness/residual stress)
 * where `Fe = π²E/λ²` is the Euler (elastic critical) stress.
 */
export function compressionCapacity(m: Member): number {
  const A = m.section.A;
  const Fy = m.material.Fy;
  const E = m.material.E;
  const r = Math.sqrt(m.section.I / A); // radius of gyration
  const lambda = (m.Kfactor * m.L0) / r; // slenderness KL/r
  const Fe = (Math.PI * Math.PI * E) / (lambda * lambda); // Euler stress
  const Fcr =
    lambda <= 4.71 * Math.sqrt(E / Fy)
      ? Math.pow(0.658, Fy / Fe) * Fy // inelastic (transition) buckling
      : 0.877 * Fe; // elastic buckling
  return A * Fcr;
}

/** Utilization of a member given its current axial force (tension +). */
export function utilization(m: Member): number {
  if (m.force >= 0) return m.force / tensionCapacity(m);
  return -m.force / compressionCapacity(m);
}

// --- member END-CONNECTION capacity (docs/engineering/17 & 07 §4) ------------
//
// A member can fail at its bolted END connection BEFORE the member itself yields
// or buckles (doc 17 §10: "a joint fails when UR_j ≥ 1 even if every attached
// member is at 60%"). We model the governing bolted limit states — bolt shear
// rupture and plate bearing — for a realistic default lattice splice detail
// sized to the section (doc 17 §1).
//
// CALIBRATION (guardrail): real connections are DESIGNED to develop the member,
// so the DEFAULT detail must never govern a healthy stock/built-up member —
// otherwise a well-designed tower would shed connections at rest (the harness's
// "surviving-member util" pass-flag is fooled by exactly this member-shedding, so
// it can't be trusted to catch it). We therefore FLOOR the default connection
// capacity at `DEVELOP_FACTOR × the member's own axial capacity`, i.e. the joint
// always develops the member it splices. This keeps every currently-standing
// tower byte-identical. The shear-out FAILURE MODE is still first-class and live:
// it fires whenever the resolved joint demand exceeds the connection capacity —
// which happens for an EXPLICITLY under-detailed joint (a future per-member
// `connectionDetail` authoring lever) or a demand beyond even the developed
// capacity — reporting failMode "shear" so the render/replay shows a bolt
// shear-out distinct from a member snap/buckle (doc 17 §9).
//
// A490 could be added as a design lever; we use A325 (F3125 Gr. A325) throughout
// as the common tower bolt (doc 17 §1.2).

/** A325 (F3125 Gr. A325) bolt ultimate tensile strength Fub (Pa) — doc 17 §1.2. */
const BOLT_FUB = 827e6;
/** Plate/gusset ultimate Fu (Pa) — A572 Gr50 plate ≈ 450 MPa — doc 07 §5. */
const PLATE_FU = 450e6;
/**
 * Nominal bolt shear stress fraction of Fub with threads EXCLUDED from the shear
 * plane: Rnv = 0.5·Fub·Ab (doc 17 §1.1 / doc 07 §4). Threads-included would be
 * 0.4; we take the common shop-detailed "X" value.
 */
const BOLT_SHEAR_FRAC = 0.5;
/** Bearing coefficient 2.4·d·t·Fu (deformation-controlled, AISC J3-6a) — doc 17 §1.1. */
const BEARING_COEF = 2.4;
/**
 * The default joint is designed to DEVELOP the member: its capacity is floored at
 * this multiple of the member's own axial capacity (doc 17 §10 — connections are
 * detailed to be stronger than the members they splice). >1 so a healthy member
 * always snaps/buckles before its default connection shears out — no regression.
 */
const DEVELOP_FACTOR = 1.25;

/**
 * A default bolted lattice-splice detail for a member, tiered by section area.
 * Bolt diameter, bolt count and splice-plate thickness all grow with the member —
 * as real detailing does. Lattice bracing/legs splice in DOUBLE shear (splice
 * plates both faces), the common tower arrangement (doc 17 §1.1/§3). This is the
 * bolted BASIS; `connectionCapacity` floors it to develop the member so the basis
 * never governs a healthy stock member (see the DEVELOP_FACTOR note above).
 */
interface BoltDetail {
  d: number; // bolt diameter (m)
  n: number; // number of bolts in the end connection
  t: number; // connected-plate/gusset thickness (m) — governs bearing
  planes: number; // shear planes per bolt (2 = double shear)
}
function defaultBoltDetail(A: number): BoltDetail {
  // tiers from cross-sectional area (m²); double shear throughout.
  if (A <= 3e-4) return { d: 0.012, n: 2, t: 0.006, planes: 2 };
  if (A <= 7e-4) return { d: 0.016, n: 2, t: 0.008, planes: 2 };
  if (A <= 13e-4) return { d: 0.016, n: 3, t: 0.01, planes: 2 };
  if (A <= 22e-4) return { d: 0.02, n: 4, t: 0.012, planes: 2 };
  if (A <= 40e-4) return { d: 0.024, n: 4, t: 0.016, planes: 2 };
  return { d: 0.024, n: 6, t: 0.02, planes: 2 };
}

/** The nominal member axial capacity used to size/develop the connection (N). */
function memberAxialCapacity(m: Member): number {
  // develop the larger of tension yield and compression capacity.
  return Math.max(tensionCapacity(m), compressionCapacity(m));
}

/**
 * End-connection capacity of a member (N). The bolted BASIS is the min over the
 * governing limit states × bolt count (doc 17 §10 eq 15):
 *   - bolt shear rupture   Rnv = 0.5·Fub·Ab per shear plane   (doc 17 eq 1)
 *   - plate bearing        Rn  = 2.4·d·t·Fu                    (doc 17 eq 3)
 * The returned capacity is FLOORED to develop the member (DEVELOP_FACTOR × member
 * axial capacity) so a well-detailed stock joint never governs a healthy member —
 * the guardrail requirement (no shedding of connections at rest). An explicitly
 * under-detailed joint (future `connectionDetail` lever) would pass a weaker basis
 * and let the connection govern → failMode "shear".
 *
 * Tension-only members (guys) are excluded: their swaged end fittings develop the
 * strand's rated breaking load, already returned by `tensionCapacity` — so they
 * return Infinity (no separate connection check).
 */
export function connectionCapacity(m: Member): number {
  if (m.tensionOnly) return Infinity; // guys: swaged fitting develops the strand
  const A = m.section.A;
  const { d, n, t, planes } = defaultBoltDetail(A);
  const Ab = (Math.PI * d * d) / 4;
  const shearPerBolt = planes * BOLT_SHEAR_FRAC * BOLT_FUB * Ab;
  const bearingPerBolt = BEARING_COEF * d * t * PLATE_FU;
  const bolted = n * Math.min(shearPerBolt, bearingPerBolt);
  // develop the member: the default joint is always at least this strong.
  return Math.max(bolted, DEVELOP_FACTOR * memberAxialCapacity(m));
}
